import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Chart } from 'primereact/chart';
import ReportService from '../../services/ReportService';

const currency = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' });
const fmtMoney = (v) => currency.format(v ?? 0);

const toLocalYMD = (d) => {
    if (!(d instanceof Date)) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

export default function ProfitLossReportPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([new Date(new Date().getFullYear(), 0, 1), new Date()]);
    const [chartData, setChartData] = useState({});
    const toast = useRef(null);

    const fetchReport = async () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return;
        setLoading(true);
        try {
            const res = await ReportService.getProfitLossReport(toLocalYMD(dateRange[0]), toLocalYMD(dateRange[1]));
            const reportData = res.data.data;
            setData(reportData);

            setChartData({
                labels: reportData.map(r => r.period),
                datasets: [
                    {
                        label: 'Gelirler',
                        backgroundColor: '#10b981',
                        borderRadius: 8,
                        data: reportData.map(r => r.totalSales)
                    },
                    {
                        label: 'Giderler',
                        backgroundColor: '#ef4444',
                        borderRadius: 8,
                        data: reportData.map(r => r.totalExpenses)
                    }
                ]
            });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Rapor alınamadı' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const totals = {
        sales: data.reduce((acc, r) => acc + (r.totalSales || 0), 0),
        expenses: data.reduce((acc, r) => acc + (r.totalExpenses || 0), 0)
    };
    const netProfit = totals.sales - totals.expenses;

    const header = (
        <div className="flex items-center gap-3 py-2">
            <div className="flex flex-col gap-1">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Analiz Dönemi</span>
                <Calendar value={dateRange} onChange={(e) => setDateRange(e.value)} selectionMode="range" readOnlyInput dateFormat="mm.yy" view="month" className="w-full sm:w-64" showIcon inputClassName="font-bold text-slate-700 py-2" />
            </div>
            <Button icon="pi pi-search" label="GÜNCELLE" onClick={fetchReport} loading={loading} className="!bg-blue-600 border-none hover:!bg-blue-700 px-6 h-[40px] rounded-xl font-black text-[11px] shadow-lg shadow-blue-600/20 text-white mt-5" />
        </div>
    );

    const chartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 0.8,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    font: { size: 12, weight: 'bold' }
                }
            },
            tooltip: {
                padding: 12,
                backgroundColor: '#1e293b',
                titleFont: { size: 14, weight: 'bold' },
                bodyFont: { size: 13 }
            }
        },
        scales: {
            x: {
                grid: { display: false }
            },
            y: {
                beginAtZero: true,
                grid: { color: '#f1f5f9' }
            }
        }
    };

    return (
        <div className="p-2 md:p-4  bg-[#f8fafc] min-h-screen animate-fade-in">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

            {/* Elite Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2rem] !bg-indigo-600 flex items-center justify-center shadow-2xl shadow-indigo-200">
                        <i className="pi pi-percentage text-white text-3xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight tracking-tighter uppercase">Kar/Zarar Analizi</h1>
                        <div className="flex items-center gap-3 mt-1">
                            <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                            <p className="text-slate-500 font-bold text-sm uppercase tracking-widest italic">Finansal Performans Özeti</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-4">
                {[
                    { label: "Toplam Gelir", value: totals.sales, color: "text-emerald-600", bg: "bg-emerald-50", icon: "pi-arrow-up-right" },
                    { label: "Toplam Gider", value: totals.expenses, color: "text-rose-600", bg: "bg-rose-50", icon: "pi-arrow-down-right" },
                    { label: "Net Kar/Zarar", value: netProfit, color: netProfit >= 0 ? "text-blue-600" : "text-amber-600", bg: netProfit >= 0 ? "bg-blue-50" : "bg-amber-50", icon: "pi-wallet" }
                ].map((s, i) => (
                    <div key={i} className={`${s.bg} p-4 rounded-[2.5rem] border border-white/50 shadow-xl flex items-center justify-between transition-all hover:scale-[1.02]`}>
                        <div>
                            <p className={`text-[10px] font-black ${s.color} uppercase tracking-[0.2em] mb-2`}>{s.label}</p>
                            <h3 className={`text-3xl font-black ${s.color} tracking-tighter`}>{fmtMoney(s.value)}</h3>
                        </div>
                        <div className={`w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center ${s.color}`}>
                            <i className={`pi ${s.icon} text-2xl`} />
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                 <div className="lg:col-span-4">
                    <div className="bg-white shadow-2xl shadow-slate-200/50 border border-slate-200 rounded-[2.5rem] p-4 h-full">
                         <div className="flex items-center justify-between mb-8">
                            <h3 className="font-black text-slate-800 uppercase tracking-tighter text-xl">Performans Grafiği</h3>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                                    <span className="text-[10px] font-black text-slate-400 uppercase">Gelir</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-rose-500"></span>
                                    <span className="text-[10px] font-black text-slate-400 uppercase">Gider</span>
                                </div>
                            </div>
                        </div>
                        <div className="h-[450px]">
                            <Chart type="bar" data={chartData} options={chartOptions} height="450px" />
                        </div>
                    </div>
                 </div>

                 <div className="lg:col-span-8">
                    <div className="bg-white shadow-2xl shadow-slate-200/50 border border-slate-200 rounded-[2.5rem] overflow-hidden">
                        <div className="bg-slate-50/50 border-b border-slate-200 px-4 py-2">
                            <h3 className="font-black text-slate-800 uppercase tracking-tighter text-lg">Dönemsel Veriler</h3>
                        </div>
                        <div className="p-2 ">
                            <DataTable
                                value={data}
                                loading={loading}
                                header={header}
                                className="p-datatable-sm "
                                emptyMessage="Kayıt bulunamadı."
                                paginator
                                rows={15}
                            >
                                <Column field="period" header="DÖNEM" body={(r) => <span className="font-black text-slate-600 uppercase tracking-tighter">{r.period}</span>} headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3" />
                                <Column field="totalSales" header="GELİR" body={(r) => <span className="text-emerald-600 font-bold">{fmtMoney(r.totalSales)}</span>} style={{ textAlign: 'right' }} headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 text-right" />
                                <Column field="totalExpenses" header="GİDER" body={(r) => <span className="text-rose-600 font-bold">{fmtMoney(r.totalExpenses)}</span>} style={{ textAlign: 'right' }} headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 text-right" />
                                <Column header="NET" body={(r) => {
                                    const net = r.totalSales - r.totalExpenses;
                                    return <span className={`font-black ${net >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>{fmtMoney(net)}</span>
                                }} style={{ textAlign: 'right' }} headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 text-right" />
                            </DataTable>
                        </div>
                    </div>
                 </div>
            </div>
        </div>
    );
}
